import { Box, Typography } from "@mui/material";
import { Circle as CircleIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

// Comprehensive Step Icon Mapping - Complete implementation
// Extracted from Flows.jsx (was inline there) so both the Flows table's Steps
// column AND the Explore tab's template cards/detail page share the exact
// same read-only "step preview" rendering - no live canvas, just a row of
// piece icons, matching real Activepieces' own PieceIconList component for
// the same job in its official template gallery.
const PIECE_ICON_MAPPING = {
  // Piece-based mappings
  "@activepieces/piece-gmail": "https://cdn.activepieces.com/pieces/gmail.png",
  "@activepieces/piece-google-sheets":
    "https://cdn.activepieces.com/pieces/google-sheets.png",
  "@activepieces/piece-http": "https://cdn.activepieces.com/pieces/webhook.svg",
  "@activepieces/piece-smtp": "https://cdn.activepieces.com/pieces/smtp.png",
  "@activepieces/piece-store": "https://cdn.activepieces.com/pieces/store.png",
  "@activepieces/piece-deepseek":
    "https://cdn.activepieces.com/pieces/deepseek.png",
  "@activepieces/piece-github": "https://cdn.activepieces.com/pieces/github.png",
  "@activepieces/piece-open-router":
    "https://cdn.activepieces.com/pieces/open-router.png",
  "@activepieces/piece-postgres":
    "https://cdn.activepieces.com/pieces/postgres.png",
  "@activepieces/piece-whatsapp":
    "https://cdn.activepieces.com/pieces/whatsapp.png",
  "@activepieces/piece-subflows": "https://cdn.activepieces.com/pieces/flows.svg",
  "@activepieces/piece-slack": "https://cdn.activepieces.com/pieces/slack.png",
  "@activepieces/piece-discord": "https://cdn.activepieces.com/pieces/discord.png",
  "@activepieces/piece-twitter": "https://cdn.activepieces.com/pieces/twitter.png",
  "@activepieces/piece-telegram":
    "https://cdn.activepieces.com/pieces/telegram.png",
  "@activepieces/piece-notion": "https://cdn.activepieces.com/pieces/notion.png",
  "@activepieces/piece-airtable":
    "https://cdn.activepieces.com/pieces/airtable.png",
  "@activepieces/piece-trello": "https://cdn.activepieces.com/pieces/trello.png",
  "@activepieces/piece-asana": "https://cdn.activepieces.com/pieces/asana.png",
  "@activepieces/piece-jira": "https://cdn.activepieces.com/pieces/jira.png",
  "@activepieces/piece-linear": "https://cdn.activepieces.com/pieces/linear.png",
  "@activepieces/piece-monday": "https://cdn.activepieces.com/pieces/monday.png",
  "@activepieces/piece-clickup":
    "https://cdn.activepieces.com/pieces/clickup.png",
  "@activepieces/piece-salesforce":
    "https://cdn.activepieces.com/pieces/salesforce.png",
  "@activepieces/piece-hubspot":
    "https://cdn.activepieces.com/pieces/hubspot.png",
  "@activepieces/piece-pipedrive":
    "https://cdn.activepieces.com/pieces/pipedrive.png",
  "@activepieces/piece-zapier": "https://cdn.activepieces.com/pieces/zapier.png",
  "@activepieces/piece-make": "https://cdn.activepieces.com/pieces/make.png",
  "@activepieces/piece-aws": "https://cdn.activepieces.com/pieces/aws.png",
  "@activepieces/piece-azure": "https://cdn.activepieces.com/pieces/azure.png",
  "@activepieces/piece-gcp": "https://cdn.activepieces.com/pieces/gcp.png",
  "@activepieces/piece-stripe": "https://cdn.activepieces.com/pieces/stripe.png",
  "@activepieces/piece-paypal": "https://cdn.activepieces.com/pieces/paypal.png",
  "@activepieces/piece-shopify":
    "https://cdn.activepieces.com/pieces/shopify.png",
  "@activepieces/piece-woocommerce":
    "https://cdn.activepieces.com/pieces/woocommerce.png",
  "@activepieces/piece-magento":
    "https://cdn.activepieces.com/pieces/magento.png",
  "@activepieces/piece-calendar":
    "https://cdn.activepieces.com/pieces/google-calendar.png",
  "@activepieces/piece-drive":
    "https://cdn.activepieces.com/pieces/google-drive.png",
  "@activepieces/piece-docs":
    "https://cdn.activepieces.com/pieces/google-docs.png",
  "@activepieces/piece-forms":
    "https://cdn.activepieces.com/pieces/google-forms.png",
  "@activepieces/piece-meet":
    "https://cdn.activepieces.com/pieces/google-meet.png",
  "@activepieces/piece-youtube":
    "https://cdn.activepieces.com/pieces/youtube.png",
  "@activepieces/piece-dropbox":
    "https://cdn.activepieces.com/pieces/dropbox.png",
  "@activepieces/piece-onedrive":
    "https://cdn.activepieces.com/pieces/onedrive.png",
  "@activepieces/piece-box": "https://cdn.activepieces.com/pieces/box.png",
  "@activepieces/piece-ftp": "https://cdn.activepieces.com/pieces/ftp.png",
  "@activepieces/piece-sftp": "https://cdn.activepieces.com/pieces/sftp.png",
  "@activepieces/piece-mysql": "https://cdn.activepieces.com/pieces/mysql.png",
  "@activepieces/piece-mongodb":
    "https://cdn.activepieces.com/pieces/mongodb.png",
  "@activepieces/piece-redis": "https://cdn.activepieces.com/pieces/redis.png",
  "@activepieces/piece-elasticsearch":
    "https://cdn.activepieces.com/pieces/elasticsearch.png",
  "@bunyan/chat": `${window.env.REACT_APP_BASE_URL}/logo.png`,
  // Built-in types
  LOOP_ON_ITEMS: "https://cdn.activepieces.com/pieces/loop.svg",
  ROUTER: "https://cdn.activepieces.com/pieces/branch.svg",
  CODE: "https://cdn.activepieces.com/pieces/code.svg",
  WEBHOOK: "https://cdn.activepieces.com/pieces/webhook.svg",
  JSON: "https://cdn.activepieces.com/pieces/json.svg",
  PDF: "https://cdn.activepieces.com/pieces/pdf.svg",
};

// Recursively flattens a flow-version trigger tree (trigger + nextAction /
// firstLoopAction / children / settings.branches[].firstAction) into a flat
// list of {name, type, displayName, pieceName, logoUrl} steps.
function extractStepsFromFlow(trigger) {
  if (!trigger) {
    return [];
  }

  const steps = [];

  const traverseSteps = (step) => {
    if (!step) {
      return;
    }

    steps.push({
      name: step.name,
      type: step.type,
      displayName: step.displayName,
      pieceName: step.settings?.pieceName,
      logoUrl: step.settings?.inputUiInfo?.customizedInputs?.logoUrl,
    });

    if (step.nextAction) {
      traverseSteps(step.nextAction);
    }

    if (step.firstLoopAction) {
      traverseSteps(step.firstLoopAction);
    }

    if (step.children && Array.isArray(step.children)) {
      step.children.forEach((child) => traverseSteps(child));
    }

    if (
      step.settings &&
      step.settings.branches &&
      Array.isArray(step.settings.branches)
    ) {
      step.settings.branches.forEach((branch) => {
        if (branch.firstAction) {
          traverseSteps(branch.firstAction);
        }
      });
    }
  };

  try {
    traverseSteps(trigger);
    return steps;
  } catch (error) {
    console.error("Error in extractStepsFromFlow:", error);
    return [];
  }
}

// Exported so callers that only have piece NAMES (e.g. a template gallery
// card's `pieces: string[]`, with no full trigger/step tree) can still render
// icons without walking a step tree that doesn't exist yet.
export function getPieceIconUrl(pieceName) {
  return (
    PIECE_ICON_MAPPING[pieceName] ||
    "https://cdn.activepieces.com/pieces/webhook.svg"
  );
}

function getStepIconUrl(step) {
  if (step.logoUrl) {
    return step.logoUrl;
  }

  if (step.pieceName && PIECE_ICON_MAPPING[step.pieceName]) {
    return PIECE_ICON_MAPPING[step.pieceName];
  }

  if (PIECE_ICON_MAPPING[step.type]) {
    return PIECE_ICON_MAPPING[step.type];
  }

  return "https://cdn.activepieces.com/pieces/webhook.svg";
}

// The row-of-icons + "+N" overflow badge markup shared by FlowStepIcons and
// PieceIconRow. Deliberately just the rendering: dedup/slice/error-handling
// differ enough between the two callers (try/catch + i18n error state vs.
// none) to stay in each component rather than be forced into one shape here.
function IconRow({ items, remainingCount, iconSize, onImageError }) {
  return (
    <>
      {items.map((item) => (
        <Box
          key={item.key}
          component="img"
          src={item.iconUrl}
          alt={item.alt}
          title={item.alt}
          onError={
            onImageError
              ? (e) => onImageError(e, item)
              : (e) => {
                  e.target.style.display = "none";
                }
          }
          sx={{
            width: iconSize,
            height: iconSize,
            objectFit: "contain",
            borderRadius: "4px",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            padding: "2px",
            marginInlineEnd: 0.5,
          }}
        />
      ))}
      {remainingCount > 0 && (
        <Typography
          variant="body2"
          component="span"
          sx={{
            marginInlineStart: 0.5,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          +{remainingCount}
        </Typography>
      )}
    </>
  );
}

/**
 * Read-only row of a flow/template's step icons, deduped, with a "+N"
 * overflow count. `trigger` is a flow-version-shaped trigger object (i.e.
 * `flow.version.trigger` for a real flow, or a template's `flows[0].trigger`).
 */
export function FlowStepIcons({ trigger, maxVisible = 2, iconSize = 20 }) {
  const { t } = useTranslation();

  if (!trigger) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <CircleIcon
          sx={{ marginInlineEnd: 0.5, fontSize: 16, color: "text.disabled" }}
        />
        <CircleIcon
          sx={{ marginInlineEnd: 0.5, fontSize: 16, color: "text.disabled" }}
        />
      </Box>
    );
  }

  try {
    const steps = extractStepsFromFlow(trigger);

    if (steps.length === 0) {
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CircleIcon
            sx={{ marginInlineEnd: 0.5, fontSize: 16, color: "text.disabled" }}
          />
        </Box>
      );
    }

    const uniqueSteps = steps.filter(
      (step, index, self) =>
        index === self.findIndex((s) => getStepIconUrl(s) === getStepIconUrl(step)),
    );

    const visibleSteps = uniqueSteps.slice(0, maxVisible);
    const remainingCount = uniqueSteps.length - maxVisible;
    const items = visibleSteps.map((step, index) => ({
      key: index,
      iconUrl: getStepIconUrl(step),
      alt: step.displayName || step.type,
    }));

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconRow
          items={items}
          remainingCount={remainingCount}
          iconSize={iconSize}
          onImageError={(e, item) => {
            console.error("Failed to load icon:", item.iconUrl);
            e.target.style.display = "none";
          }}
        />
        {visibleSteps.length === 0 && (
          <Typography
            variant="body2"
            component="span"
            sx={{ fontSize: "0.75rem", color: "text.disabled" }}
          >
            {t("common.noSteps")}
          </Typography>
        )}
      </Box>
    );
  } catch (error) {
    console.error("Error in FlowStepIcons:", error);
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <CircleIcon
          sx={{ marginInlineEnd: 0.5, fontSize: 16, color: "error.main" }}
        />
        <Typography
          variant="body2"
          component="span"
          sx={{ fontSize: "0.75rem", color: "error.main" }}
        >
          {t("common.error")}
        </Typography>
      </Box>
    );
  }
}

/**
 * Same visual shape as FlowStepIcons, but for callers that only have piece
 * NAMES (e.g. a template gallery card's `pieces: string[]`) rather than a
 * full trigger/step tree - the Explore tab's list view deliberately doesn't
 * fetch per-template trigger data (kept out of the list response to avoid
 * embedding every template's full step JSON in one large payload), so cards
 * render from `pieces` while the detail page uses <FlowStepIcons/> directly.
 */
export function PieceIconRow({ pieces, maxVisible = 2, iconSize = 20 }) {
  if (!pieces || pieces.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <CircleIcon
          sx={{ marginInlineEnd: 0.5, fontSize: 16, color: "text.disabled" }}
        />
      </Box>
    );
  }

  const uniquePieces = pieces.filter(
    (name, index, self) =>
      index === self.findIndex((n) => getPieceIconUrl(n) === getPieceIconUrl(name)),
  );
  const visiblePieces = uniquePieces.slice(0, maxVisible);
  const remainingCount = uniquePieces.length - maxVisible;
  const items = visiblePieces.map((pieceName) => ({
    key: pieceName,
    iconUrl: getPieceIconUrl(pieceName),
    alt: pieceName,
  }));

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconRow items={items} remainingCount={remainingCount} iconSize={iconSize} />
    </Box>
  );
}
